import { useState, useEffect, useCallback } from 'react';
import { dashboardService } from '@/services/dashboard.service';
import type {
  DashboardStatistics,
  RevenueDataPoint,
  SalesTypeData,
  Activity,
  PendingApproval,
  ProjectStatus,
  TopCustomerRow,
} from '@/services/dashboard.service';
import {
  getUnifiedDashboardActionSummary,
  type UnifiedDashboardActionSummary,
} from '@/services/unifiedDashboard.service';
import { emptySalesDashboardSummary } from '@/services/salesDashboard.service';
import { emptySupplyChainDashboardSummary } from '@/services/supplyChainDashboard.service';
import { emptyFinanceDashboardSummary } from '@/services/financeDashboard.service';

export interface HomeDashboardData {
  unified: UnifiedDashboardActionSummary;
  statistics: DashboardStatistics | null;
  revenueTrend: RevenueDataPoint[];
  salesByType: SalesTypeData[];
  activities: Activity[];
  pendingApprovals: PendingApproval[];
  projectStatus: ProjectStatus[];
  topCustomers: TopCustomerRow[];
}

const emptyUnified: UnifiedDashboardActionSummary = {
  finance: emptyFinanceDashboardSummary(),
  supply_chain: emptySupplyChainDashboardSummary(),
  sales: emptySalesDashboardSummary(),
};

function normalizeArray<T>(v: unknown): T[] {
  return Array.isArray(v) ? (v as T[]) : [];
}

export function useHomeDashboardData() {
  const [data, setData] = useState<HomeDashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [updatedAt, setUpdatedAt] = useState<Date | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [
        unified,
        statistics,
        revenueTrend,
        salesByType,
        activities,
        pendingApprovals,
        projectStatus,
        topCustomers,
      ] = await Promise.all([
        getUnifiedDashboardActionSummary().catch(() => emptyUnified),
        dashboardService.getStatistics().catch(() => null),
        dashboardService.getRevenueTrend().catch(() => []),
        dashboardService.getSalesByType().catch(() => []),
        dashboardService.getRecentActivities(10).catch(() => []),
        dashboardService.getPendingApprovals().catch(() => []),
        dashboardService.getProjectStatus().catch(() => []),
        dashboardService.getTopCustomers(5).catch(() => []),
      ]);

      setData({
        unified,
        statistics,
        revenueTrend: normalizeArray<RevenueDataPoint>(revenueTrend),
        salesByType: normalizeArray<SalesTypeData>(salesByType),
        activities: normalizeArray<Activity>(activities),
        pendingApprovals: normalizeArray<PendingApproval>(pendingApprovals),
        projectStatus: normalizeArray<ProjectStatus>(projectStatus),
        topCustomers: normalizeArray<TopCustomerRow>(topCustomers),
      });
      setUpdatedAt(new Date());
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load dashboard');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { data, loading, error, updatedAt, refresh };
}
